import { useMutation } from '@tanstack/react-query'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { useErrorToast } from '@/hooks/useErrorToast'
import { cn } from '@/lib/utils'
import { Check } from '@/shared/icons/outline'
import { Button, Card, MotionDiv, Typography } from '@/shared/ui'

import { sendContractorReminder } from '../../api'
import {
  ContractorDetails,
  ContractorPaymentMethodState,
  ContractState,
} from '../../types'

type Props = {
  contractor?: ContractorDetails
}

export const ContractActionsSteps = ({ contractor }: Props) => {
  const intl = useIntl()
  const notifyError = useErrorToast()
  const [, setSearchParams] = useSearchParams()

  const { mutateAsync: sendReminderAsync, isPending: isSendingReminder } =
    useMutation({ mutationFn: sendContractorReminder })

  const latestContract = contractor?.contracts[0]

  const onSendReminder = async () => {
    if (!contractor?.id) {
      return
    }

    try {
      await sendReminderAsync({ id: contractor.id })

      toast.success(
        intl.formatMessage({
          id: 'team.member.reminder.success',
          defaultMessage: 'Invitation reminder sent successfully',
        }),
      )
    } catch (error) {
      notifyError(error)
    }
  }

  const firstStepCompleted =
    contractor?.paymentMethod.state === ContractorPaymentMethodState.LINKED

  const hasAmendedContract = !!contractor?.contracts.find(
    (contract) => contract.state === ContractState.AMENDED,
  )

  const secondStepCompleted =
    latestContract?.state !== ContractState.ACTION_NEEDED

  if (firstStepCompleted && secondStepCompleted) {
    return null
  }

  return (
    <MotionDiv className="flex flex-col gap-4 pb-8">
      {hasAmendedContract ? (
        <Card
          size="upload"
          className={cn(
            'flex flex-wrap items-center justify-between md:flex-nowrap',
            !firstStepCompleted && 'pointer-events-none',
          )}
        >
          <div className="flex flex-wrap">
            <div className="flex flex-col justify-center">
              <Typography
                bold
                className={cn(!firstStepCompleted && 'text-neutral-gray-400')}
              >
                <FormattedMessage
                  id="contractors.toDo.signAmendedContract.title"
                  defaultMessage="Sign the amended contract with {name}"
                  values={{
                    name: contractor?.personalDetails.firstName,
                  }}
                />
              </Typography>
              <Typography
                variant="body-small"
                className={cn('text-neutral-gray-600')}
              >
                <FormattedMessage
                  id="contractors.toDo.signAmendedContract.description"
                  defaultMessage="{name} has already signed their contract, we are just missing your signature to finalize the new contract"
                  values={{
                    name: contractor?.personalDetails.firstName,
                  }}
                />
              </Typography>
            </div>
          </div>

          <Button
            onClick={() => {
              setSearchParams(
                (params) => {
                  params.set('sign-contract', 'true')
                  return params
                },
                { preventScrollReset: true },
              )
            }}
          >
            <FormattedMessage
              id="actions.reviewAndSign"
              defaultMessage="Review & sign"
            />
          </Button>
        </Card>
      ) : (
        <>
          <Card
            size="upload"
            className={cn(
              'flex flex-wrap items-center justify-between',
              firstStepCompleted && 'pointer-events-none opacity-50',
            )}
          >
            <div className="flex flex-wrap">
              <div className="flex flex-col justify-center">
                <Typography bold>
                  <FormattedMessage
                    id="contractors.contract.signReminder.title"
                    defaultMessage="1. Remind {name} to sign the contract"
                    values={{
                      name: contractor?.personalDetails.firstName,
                    }}
                  />
                </Typography>
                <Typography
                  variant="body-small"
                  className="text-neutral-gray-600"
                >
                  <FormattedMessage
                    id="contractors.actions.signContract.standard.description"
                    defaultMessage="Give them a friendly reminder so they finalize the onboarding"
                  />
                </Typography>
              </div>
            </div>

            {firstStepCompleted ? (
              <Check />
            ) : (
              <Button
                onClick={onSendReminder}
                disabled={isSendingReminder}
                loading={isSendingReminder}
              >
                <FormattedMessage id="actions.remind" defaultMessage="Remind" />
              </Button>
            )}
          </Card>

          <Card
            size="upload"
            className={cn(
              'flex flex-wrap items-center justify-between',
              !firstStepCompleted && 'pointer-events-none',
            )}
          >
            <div className="flex flex-wrap">
              <div className="flex flex-col justify-center">
                <Typography
                  bold
                  className={cn(!firstStepCompleted && 'text-neutral-gray-400')}
                >
                  <FormattedMessage
                    id="contractors.actions.signContract.title"
                    defaultMessage="2. Review and sign {name}'s contract"
                    values={{
                      name: contractor?.personalDetails.firstName,
                    }}
                  />
                </Typography>
                <Typography
                  variant="body-small"
                  className={cn(
                    'text-neutral-gray-600',
                    !firstStepCompleted && 'text-neutral-gray-400',
                  )}
                >
                  <FormattedMessage
                    id="contractor.actions.signTheContract.description"
                    defaultMessage="We are just missing your signature to finalize the onboarding"
                    values={{
                      name: contractor?.personalDetails.firstName,
                    }}
                  />
                </Typography>
              </div>
            </div>

            {!firstStepCompleted ? null : (
              <Button
                onClick={() => {
                  setSearchParams(
                    (params) => {
                      params.set('sign-contract', 'true')
                      return params
                    },
                    { preventScrollReset: true },
                  )
                }}
              >
                <FormattedMessage
                  id="actions.reviewAndSign"
                  defaultMessage="Review & sign"
                />
              </Button>
            )}
          </Card>
        </>
      )}
    </MotionDiv>
  )
}
